import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import InfoSquare from 'assets/icons/info-square.svg';
import TickSquare from 'assets/icons/tick-square.svg';
import TimeSquare from 'assets/icons/time-square.svg';
import {
  DonationStatus,
  usePayrollRunFlatFileDonationsQuery,
  useRecreateDonationForTheNextPayrollRunFlatFileMutation,
} from 'generated/graphql';
import { DonationsTable as GenericDonationTable } from 'containers/PayrollGenericPage/Schedule/DonationsTable';
import { TablePaginationActionsV5 } from 'retired/TablePaginationActions';
import { Chip } from 'components/Chip';
import { RetryModal } from 'containers/PayrollGenericPage/RetryModal';

import { DonationTableFilters } from './PayrollRun';

type Props = {
  filters: DonationTableFilters;
  payrollRunFlatFileId: string;
};

const colorForStatus = {
  [DonationStatus.Pending]: undefined,
  [DonationStatus.Success]: 'success',
  [DonationStatus.Failed]: 'error',
  [DonationStatus.Reversed]: 'warning',
  [DonationStatus.Cancelled]: 'warning',
  [DonationStatus.Unresolved]: 'warning',
} as const;

const labelForStatus = (status: DonationStatus): string => {
  const labels: Record<string, string> = {
    [DonationStatus.Failed]: 'Unsuccessful',
    [DonationStatus.Success]: 'Deducted',
  };
  return status in labels ? labels[status] : status;
};

export const DonationsTable = ({ filters, payrollRunFlatFileId }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [retryModalForDonation, setRetryModalForDonation] = React.useState<string | undefined>();
  const [updateUnresolvedDonation, updateState] =
    useRecreateDonationForTheNextPayrollRunFlatFileMutation({
      onCompleted: () => setRetryModalForDonation(undefined),
    });

  const { data, loading, error, refetch } = usePayrollRunFlatFileDonationsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      payrollRunFlatFileId,
      skip: page * rowsPerPage,
      take: rowsPerPage,
      paymentStatuses: filters.paymentStatuses,
      frequency: filters.frequency,
      employeeTerm: filters.employeeTerm,
      nonprofitTerm: filters.nonprofitTerm,
    },
  });

  const renderAvatar = (donationStatus: string) => {
    switch (donationStatus) {
      case DonationStatus.Success:
        return <img src={TickSquare} alt="Tick Square" />;

      case DonationStatus.Failed:
        return <img src={InfoSquare} alt="Info" />;

      case DonationStatus.Unresolved:
        return <img src={TimeSquare} alt="Time Square" />;

      default:
        return undefined;
    }
  };

  return (
    <>
      <RetryModal
        isOpen={!!retryModalForDonation}
        onClose={() => setRetryModalForDonation(undefined)}
        onConfirm={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: false },
          }).then(() => refetch())
        }
        onMarkAsResolved={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: {
              donationId: retryModalForDonation,
              shouldCancel: false,
              markAsResolved: true,
            },
          }).then(() => refetch())
        }
        onCancelDonation={() =>
          retryModalForDonation &&
          updateUnresolvedDonation({
            variables: { donationId: retryModalForDonation, shouldCancel: true },
          }).then(() => refetch())
        }
        isLoading={updateState.loading}
        error={updateState.error}
      />
      <Stack spacing={2}>
        {error && <Alert severity="error">Failed to load donations</Alert>}
        <GenericDonationTable
          loading={loading}
          donations={data?.payrollRunFlatFile?.donations?.items}
          renderStatus={({ donation }) => {
            const isUnresolvedOrUnsuccessful =
              donation.status === DonationStatus.Unresolved ||
              donation.status === DonationStatus.Failed;
            const canRescheduleDonation = donation.status === DonationStatus.Unresolved;
            return (
              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                {isUnresolvedOrUnsuccessful && (
                  <Tooltip
                    title={
                      donation.status === DonationStatus.Failed
                        ? 'Payroll donation reconciliation has failed; please reach out to our payroll team for assistance.'
                        : 'We were unable to confirm the deduction status of this donation. Select Edit and rerun next cycle if you think it was a mistake'
                    }
                  >
                    <ReportProblemIcon
                      color={donation.status === DonationStatus.Failed ? 'error' : 'warning'}
                    />
                  </Tooltip>
                )}
                {donation.rescheduledTo ? (
                  <Chip size="small" label="Rescheduled" color="warning" />
                ) : (
                  <>
                    <Chip
                      size="small"
                      label={labelForStatus(donation.status)}
                      color={colorForStatus[donation.status]}
                      avatar={renderAvatar(donation.status)}
                      sx={{ display: 'flex', padding: '14px', img: { order: 2 } }}
                    />
                  </>
                )}
                {canRescheduleDonation && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      setRetryModalForDonation(donation.id);
                    }}
                    startIcon={<EditIcon />}
                  >
                    Edit
                  </Button>
                )}
              </Box>
            );
          }}
          footer={
            data?.payrollRunFlatFile?.donations?.count ? (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={{ borderBottom: 'none' }}
                    rowsPerPageOptions={[5, 25, 50, 100]}
                    count={data?.payrollRunFlatFile.donations.count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_event, newPage): void => setPage(newPage)}
                    onRowsPerPageChange={(event): void => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                    ActionsComponent={TablePaginationActionsV5}
                  />
                </TableRow>
              </TableFooter>
            ) : undefined
          }
        />
      </Stack>
    </>
  );
};
